import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    Box,
    Button,
    Card,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormLabel,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Radio,
    RadioGroup,
    Stack,
    SxProps,
    Theme,
    Typography,
} from '@mui/material';
import axios from 'axios';
import { differenceInDays, differenceInMonths, format, isSameDay, subDays } from 'date-fns';
import { compact, every, isNil, isUndefined, without } from 'lodash';
import { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMixpanel } from 'react-mixpanel-browser';
import { useSearchParams } from 'react-router-dom';
import { URLSearchParams } from 'url';

import { useDevice } from '@/api/devices';
import { useDisplayPreferences } from '@/api/userAccount';
import { queryClient } from '@/common/react-query';
import { PopoutButton, PopoutButtonHandle } from '@/components/PopoutButton';
import { LoadingIndicatorIcon } from '@/components/loaders/LoadingIndicatorIcon';
import { SpotterOtaInProgressAlert } from '@/components/spotter/SpotterOtaInProgressAlert';
import { BarometricPressureSection } from '@/components/spotter/historicalDataTab/BarometricPressureSection';
import { CurrentsLineChartSection } from '@/components/spotter/historicalDataTab/CurrentsLineChartSection';
import { DateRangeSelection } from '@/components/spotter/historicalDataTab/DateRangeSelection';
import { DetectionsSection } from '@/components/spotter/historicalDataTab/DetectionsSection';
import { DissolvedOxygenSection } from '@/components/spotter/historicalDataTab/DissolvedOxygenSection';
import { SalinityLineChartSection } from '@/components/spotter/historicalDataTab/SalinityLineChartSection';
import { SeaSurfaceTemperatureSection } from '@/components/spotter/historicalDataTab/SeaSurfaceTemperatureSection';
import { SignificantWaveSection } from '@/components/spotter/historicalDataTab/SignificantWaveSection';
import { SoundPressureChart } from '@/components/spotter/historicalDataTab/SoundPressureSection';
import { SpectrogramSection } from '@/components/spotter/historicalDataTab/SpectrogramSection';
import { SubsurfaceTemperatureSection } from '@/components/spotter/historicalDataTab/SubsurfaceTemperatureSection';
import { TurbiditySection } from '@/components/spotter/historicalDataTab/TurbiditySection';
import { WaterPressureSection } from '@/components/spotter/historicalDataTab/WaterPressureSection';
import { WaveDirectionSection } from '@/components/spotter/historicalDataTab/WaveDirectionSection';
import { WavePeriodSection } from '@/components/spotter/historicalDataTab/WavePeriodSection';
import { WindSpeedAndDirectionSection } from '@/components/spotter/historicalDataTab/WindSpeedAndDirectionSection';
import { InlineAlertBody } from '@/components/ui/InlineAlert';
import { Switch } from '@/components/ui/Switch';
import { ROUTE_PREFIX } from '@/config';
import { useDateRangeContext } from '@/context/DateRangeContext';
import { useSpotterContext } from '@/context/SpotterContext';
import { useViewToken } from '@/context/ViewTokenContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useNow } from '@/hooks/useNow';
import { useTzContext } from '@/hooks/useTzContext';
import { Store, useGlobalStore } from '@/store';
import { MockDataKey } from '@/utils/mockDataHelpers';

const OptionsMenu = ({ noSensorAlerts }: { noSensorAlerts?: boolean }) => {
    const mixpanel = useMixpanel();
    const isAlertLinesOn = useGlobalStore((state) => state.isAlertLinesOn);
    const setIsAlertLinesOn = useGlobalStore((state) => state.setIsAlertLinesOn);
    const mockDataFlag = useFeatureFlag('mockData');
    const processingSourceOptionsFlag = useFeatureFlag('processingSourceOptions');
    const handleAlertLinesToggle = (e: React.ChangeEvent<HTMLInputElement>, isChecked: boolean) => {
        setIsAlertLinesOn(isChecked);
        mixpanel?.track('Toggle alert line view setting', { alertLinesOn: isChecked });
    };

    const isScaleToFitAlertLinesOn = useGlobalStore((state) => state.isScaleToFitAlertLinesOn);
    const setIsScaleToFitAlertLinesOn = useGlobalStore((state) => state.setIsScaleToFitAlertLinesOn);
    const handleScaleToFitAlertLinesToggle = (e: React.ChangeEvent<HTMLInputElement>, isChecked: boolean) => {
        setIsScaleToFitAlertLinesOn(isChecked);
        mixpanel?.track('Toggle alert line view setting', { scaleToFit: isChecked });
    };

    if (noSensorAlerts) {
        // right now all our options have to do with sensor alerts, so just suppress the button entirely
        return false;
    }

    return (
        <PopoutButton
            buttonContent="Chart options"
            showStateChevron
            buttonProps={{ variant: 'outlined' }}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
        >
            <Paper
                sx={{
                    backgroundColor: 'white',
                    p: 2,
                }}
            >
                <Stack sx={{ gap: 1, maxWidth: 250 }}>
                    <FormControlLabel
                        sx={{
                            justifyContent: 'space-between',
                            gap: 2,
                            m: 0,
                            pl: 1,
                        }}
                        control={<Switch onChange={handleAlertLinesToggle} checked={isAlertLinesOn} />}
                        label="Show sensor alert lines"
                        labelPlacement="start"
                    />
                    <FormControlLabel
                        sx={{
                            justifyContent: 'space-between',
                            gap: 2,
                            m: 0,
                            pl: 1,
                        }}
                        control={
                            <Switch
                                onChange={handleScaleToFitAlertLinesToggle}
                                checked={isScaleToFitAlertLinesOn}
                                disabled={!isAlertLinesOn}
                            />
                        }
                        label="Scale y-axis to fit all sensor alert lines"
                        labelPlacement="start"
                    />
                    {processingSourceOptionsFlag && <ProcessingSourceOptions />}
                    {mockDataFlag && <MockDataOptions />}
                </Stack>
            </Paper>
        </PopoutButton>
    );
};

const MOCK_DATA_OPTIONS: Record<MockDataKey, string> = {
    dissolvedOxygen: 'Dissolved O₂',
    salinity: 'Salinity',
    soundPressure: 'Sound pressure',
    gappyData: 'Gappy data',
    gappyHdr: 'Gappy HDR',
    multiSensor: 'Multiply sensors',
    waveSpectrum: 'Wave spectrum',
    detections: 'Detections',
} as const;

const MockDataOptions = () => {
    const mockDataSwitches = useGlobalStore((state) => state.mockDataSwitches);
    const setMockDataSwitch = useGlobalStore((state) => state.setMockDataSwitch);
    const [, setSearchParams] = useSearchParams();

    const handleClickCheckbox = (key: MockDataKey, value: boolean) => {
        setMockDataSwitch(key, value);
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            let mockDataList = compact(params.get('mockData')?.split(' ')) ?? [];
            if (value) {
                mockDataList.push(key);
            } else {
                mockDataList = without(mockDataList, key);
            }
            params.set('mockData', mockDataList.join(' '));
            return params;
        });
    };

    return (
        <Card variant="outlined" sx={{ p: 1, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="minorLabel">Mock data options (dev only)</Typography>
            {Object.entries(MOCK_DATA_OPTIONS).map(([key, label]) => (
                <FormControlLabel
                    key={key}
                    sx={{ flexDirection: 'row' }}
                    control={
                        <Checkbox
                            sx={{ p: 0, ml: 0, mr: 0.5 }}
                            onChange={(_, value) => handleClickCheckbox(key as MockDataKey, value)}
                            checked={mockDataSwitches[key as MockDataKey] ?? false}
                        />
                    }
                    label={label}
                    labelPlacement="start"
                />
            ))}
            <Button onClick={() => queryClient.invalidateQueries()}>
                <RefreshIcon /> <Typography>Refresh data</Typography>
            </Button>
        </Card>
    );
};

const ProcessingSourceOptions = () => {
    const processingSource = useGlobalStore((state) => state.processingSource);
    const setProcessingSource = useGlobalStore((state) => state.setProcessingSource);

    return (
        <Card variant="outlined" sx={{ p: 1, display: 'flex', flexDirection: 'column' }}>
            <FormControl>
                <FormLabel>
                    <Typography variant="minorLabel">Processing source (internal only)</Typography>
                </FormLabel>
                <RadioGroup
                    defaultValue="best"
                    name="processing-group"
                    value={processingSource}
                    onChange={(event) => setProcessingSource(event.target.value as Store['processingSource'])}
                    sx={{ '& .MuiRadio-root': { p: 0, ml: 3, mr: 0.5 } }}
                >
                    <FormControlLabel value="best" control={<Radio />} label="Best available" />
                    <FormControlLabel value="embedded" control={<Radio />} label="Embedded" />
                    <FormControlLabel value="hdr" control={<Radio />} label="HDR" />
                </RadioGroup>
            </FormControl>
        </Card>
    );
};

const ObservationOtaAlert = () => {
    const { endTs } = useDateRangeContext();
    const now = useNow(300);
    const tz = useTzContext();

    if (isNil(endTs) || !isSameDay(now, endTs, tz)) {
        return null;
    }

    return (
        <SpotterOtaInProgressAlert dismissKey="observation-data" sx={{ mb: 4 }}>
            <InlineAlertBody testid="ota-in-progress-alert">
                Some data during this time period may be missing or delayed appearing on this page.
            </InlineAlertBody>
        </SpotterOtaInProgressAlert>
    );
};

export const HistoricalDataTabContent = ({
    sx,
    noSensorAlerts,
    noCsvDownload,
}: {
    sx: SxProps<Theme>;
    noSensorAlerts?: boolean;
    noCsvDownload?: boolean;
}) => {
    const { viaOceansApi } = useSpotterContext();
    const mockDataFlag = useFeatureFlag('mockData');
    const mockDataSwitches = useGlobalStore((state) => state.mockDataSwitches);
    const setMockDataSwitch = useGlobalStore((state) => state.setMockDataSwitch);
    const [searchParams] = useSearchParams();

    useEffect(() => {
        if (mockDataFlag && searchParams.get('mockData')?.length && every(mockDataSwitches, isUndefined)) {
            searchParams
                .get('mockData')
                ?.split(' ')
                .forEach((key) => setMockDataSwitch(key as MockDataKey, true));
            queryClient.invalidateQueries();
        }
    }, [mockDataSwitches, searchParams, mockDataFlag, setMockDataSwitch]);

    return (
        <Stack sx={{ justifyContent: 'center', overflow: 'hidden', ...sx }}>
            <Stack
                direction="row"
                sx={{
                    gap: 1,
                    mb: 4,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    width: '100%',
                }}
            >
                <DateRangeSelection />
                <Stack
                    direction="row"
                    sx={{
                        gap: 1,
                        alignItems: 'center',
                        justifySelf: 'end',
                    }}
                >
                    <OptionsMenu noSensorAlerts={noSensorAlerts} />
                    {!noCsvDownload && <DownloadButton />}
                </Stack>
            </Stack>
            <ObservationOtaAlert />
            <SignificantWaveSection />
            <WavePeriodSection />
            <WaveDirectionSection />
            {!viaOceansApi && (
                <>
                    <WindSpeedAndDirectionSection />
                    <BarometricPressureSection />
                    <SeaSurfaceTemperatureSection />
                    <SubsurfaceTemperatureSection />
                    <DetectionsSection />
                    <SpectrogramSection />
                    <SoundPressureChart />
                    <DissolvedOxygenSection />
                    <SalinityLineChartSection />
                    <CurrentsLineChartSection />
                    <TurbiditySection />
                    <WaterPressureSection />
                </>
            )}
        </Stack>
    );
};

const CSV_DOWNLOAD_MAX_MONTHS_NEW = 6;
const CSV_DOWNLOAD_MAX_MONTHS_CLASSIC = 1;

const DownloadButton = () => {
    const { data: device } = useDevice();
    const spotterId = device?.spotterId;
    const popoutRef = useRef<PopoutButtonHandle>(null);
    const { data: displayPreferences } = useDisplayPreferences();
    const mixpanel = useMixpanel();
    const { dateRange } = useDateRangeContext();
    const rangeInDays = dateRange ? differenceInDays(dateRange.endDate, dateRange.startDate) : null;
    const { viewToken } = useViewToken();
    const [isDownloading, setDownloading] = useState(false);

    const onDownloadClick = useCallback(
        async (e: MouseEvent<HTMLAnchorElement>) => {
            if (!dateRange || isDownloading) {
                return;
            }
            setDownloading(true);
            const type = e.currentTarget.textContent!;
            const downloadHref = e.currentTarget.href;
            popoutRef.current?.close();
            mixpanel?.track('Download CSV', {
                type,
                spotterId,
                rangeInDays,
            });
            e.preventDefault();

            let blobUrl: string | null = null;
            let blobLink: HTMLAnchorElement | null = null;
            try {
                const response = await axios.get(downloadHref, {
                    responseType: 'blob',
                    withCredentials: !viewToken,
                    headers: {
                        ...(viewToken && { view_token: viewToken }),
                    },
                });

                if (response.status !== 200) {
                    throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
                }

                blobUrl = window.URL.createObjectURL(response.data);
                blobLink = document.createElement('a');
                blobLink.href = blobUrl;
                const contentDispositionMatch = response.headers['content-disposition']?.match(/filename="(.*?)"/);
                blobLink.download =
                    contentDispositionMatch?.[1] ??
                    `${spotterId}_${format(dateRange.startDate, 'yyyy-MM-dd')}_${format(dateRange.endDate, 'yyyy-MM-dd')}_${type.toLowerCase().replaceAll(/\W/g, '-')}.csv`;
                document.body.appendChild(blobLink);
                blobLink.click();
            } catch (error) {
                console.error('Download error:', error);
                // if something went wrong up there, try just getting it the old-fashioned way.
                window.open(downloadHref, '_blank');
            } finally {
                setDownloading(false);
                if (blobLink && blobLink.parentElement) {
                    blobLink.parentElement.removeChild(blobLink);
                }
                if (blobUrl) {
                    window.URL.revokeObjectURL(blobUrl);
                }
            }
        },
        [dateRange, isDownloading, mixpanel, rangeInDays, spotterId, viewToken]
    );

    const queryParamString = useMemo(() => {
        if (!dateRange) {
            return '';
        }
        const params: URLSearchParams = new URLSearchParams();
        params.set('spotterId', spotterId!);
        params.set('startDate', dateRange.startDate.toISOString());
        params.set('endDate', dateRange.endDate.toISOString());
        params.set('processingSources', 'all');

        return params.toString();
    }, [dateRange, spotterId]);

    const tooManyMonthsMessage = useMemo(() => {
        if (!dateRange) return null;

        const months = differenceInMonths(subDays(dateRange.endDate, 1), dateRange.startDate);
        if (displayPreferences?.csvFormat === '2025-02') {
            if (months >= CSV_DOWNLOAD_MAX_MONTHS_NEW) {
                return `Maximum date range for CSV download is ${CSV_DOWNLOAD_MAX_MONTHS_NEW} months`;
            }
            return null;
        }
        if (months >= CSV_DOWNLOAD_MAX_MONTHS_CLASSIC) {
            return (
                <>
                    Maximum date range for CSV download is {CSV_DOWNLOAD_MAX_MONTHS_CLASSIC} month.{' '}
                    <a href={`${ROUTE_PREFIX}/account#preferences`}>Update to the latest CSV data format</a> to enable
                    wider ranges
                </>
            );
        }
    }, [displayPreferences?.csvFormat, dateRange]);

    return (
        <Stack direction="column" sx={{ alignItems: 'center' }}>
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                <PopoutButton
                    ref={popoutRef}
                    buttonContent={
                        <>
                            <Box sx={{ position: 'relative', left: '-6px', display: 'flex' }}>
                                <LoadingIndicatorIcon isLoading={isDownloading} color="inherit">
                                    <DownloadIcon />
                                </LoadingIndicatorIcon>
                            </Box>
                            Download data CSV
                        </>
                    }
                    buttonProps={{
                        variant: 'contained',
                        disabled: !dateRange || !!tooManyMonthsMessage || isDownloading,
                        sx: { my: 0.5 },
                    }}
                >
                    <List>
                        <ListItemButton
                            component="a"
                            onClick={onDownloadClick}
                            href={`${process.env.REACT_APP_API_URL}/fetch/download?${queryParamString}`}
                            download
                        >
                            <ListItemText>Embedded history</ListItemText>
                        </ListItemButton>
                        {(device?.externalHardware?.bridge || device?.isSmartMooring) && (
                            <ListItemButton
                                component="a"
                                onClick={onDownloadClick}
                                href={`${process.env.REACT_APP_API_URL}/fetch/download-sensor-data?${queryParamString}`}
                                download
                            >
                                <ListItemText>Smart Mooring history</ListItemText>
                            </ListItemButton>
                        )}
                    </List>
                </PopoutButton>
            </Stack>

            {!!tooManyMonthsMessage && (
                <Typography
                    variant="minorLabel"
                    sx={{
                        maxWidth: '14em',
                        /* don't want to mess with the alignment of the button above us; hopefully there's enough space */
                        height: 0,
                    }}
                >
                    {tooManyMonthsMessage}
                </Typography>
            )}
        </Stack>
    );
};
